import { z } from 'zod/v4';
import { USER_PREFERENCE_KEYS } from '../../constants.js';

const NotificationToggleSchema = z.object({ enabled: z.boolean() });

// Plain `z.object` (not `z.strictObject`) to match the legacy behaviour: the
// AJV schema also declared `additionalProperties: false`, but Fastify's
// default schema compiler runs with `removeAdditional: true`
// (node_modules/fastify/build/build-validation.js), so unknown keys were
// silently stripped, never rejected. `z.strictObject` throws on unknown keys
// instead of stripping them, which 400'd real callers (e.g. frontend-v2's
// `useTablePreferences` sends extra `vis`/`size`/`sorting`/... fields on
// `savedTablePreferences`) that the legacy route accepted fine.
const UserEmailNotificationPreferenceSchema = z.object({
  realtime: NotificationToggleSchema,
  daily: NotificationToggleSchema,
  weekly: NotificationToggleSchema.optional(),
});

const UserLanguagePreferenceSchema = z.object({
  language: z.string().min(1).max(3),
});

const ColumnReorderingItemSchema = z.object({
  column: z.string().min(1),
  show: z.boolean(),
  width: z.number().min(0),
});

const SavedTableSettingSchema = z.object({
  table: z.string().optional(),
  rowsPerPage: z.number(),
  columnReordering: z.array(ColumnReorderingItemSchema).nullable().optional(),
  filter: z.record(z.string(), z.unknown()).nullable().optional(),
});
const UserSavedTableSettingsPreferenceSchema = z.array(SavedTableSettingSchema);

const RetracedUserEmailNotificationPreferenceSchema = z.object({
  duplicate_company: z.boolean(),
  user_created: z.boolean(),
  claim_ready_for_review: z.boolean(),
  shopify_customer_data_request: z.boolean(),
  shopify_customer_redact_request: z.boolean(),
  shopify_shop_redact_request: z.boolean(),
  user_login_blocked: z.boolean(),
});

const SavedAnalyticsFilterSchema = z.object({
  report: z.string(),
  filter: z.record(z.string(), z.unknown()).nullable().optional(),
});
const UserSavedAnalyticsFilterPreferenceSchema = z.array(
  SavedAnalyticsFilterSchema,
);

/** When the user last answered "Not now" to the passkey offer after an email-code sign-in. */
const PasskeyPromptPreferenceSchema = z.object({
  dismissedAt: z.iso.datetime(),
});

// A preference `value` matches ANY of these known shapes — the legacy schema
// never ties the shape to the given `key`, so we preserve that laxity here.
const PreferenceValueSchema = z.union([
  UserEmailNotificationPreferenceSchema,
  UserLanguagePreferenceSchema,
  UserSavedTableSettingsPreferenceSchema,
  RetracedUserEmailNotificationPreferenceSchema,
  UserSavedAnalyticsFilterPreferenceSchema,
  PasskeyPromptPreferenceSchema,
]);

export const PatchPreferencesParamsSchema = z.object({
  id: z.string(),
});
export type PatchPreferencesParams = z.infer<
  typeof PatchPreferencesParamsSchema
>;

// `email` is the legacy misnomer: the value is the stored `users.email_sha256`
// hash, not a plaintext email. Preserved as-is — see get-by-email-sha.ts.
export const PatchPreferencesQuerySchema = z.object({
  email: z.string().optional(),
});
export type PatchPreferencesQuery = z.infer<typeof PatchPreferencesQuerySchema>;

export const PatchPreferencesBodySchema = z.array(
  z.object({
    key: z.enum(USER_PREFERENCE_KEYS),
    value: PreferenceValueSchema,
  }),
);
export type PatchPreferencesBody = z.infer<typeof PatchPreferencesBodySchema>;

export const PatchPreferencesResponseSchema = z.record(z.string(), z.unknown());
export type PatchPreferencesResponseView = z.infer<
  typeof PatchPreferencesResponseSchema
>;
